import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PaperClipIcon, XMarkIcon, DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { attachmentService } from '@/services/attachmentService';

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
  isInlineImage?: boolean;
}

interface FileUploadProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  onInsertInline?: (file: UploadedFile) => void;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  acceptedFileTypes?: string[];
  disabled?: boolean;
  className?: string;
  showPreview?: boolean;
  allowInlineImages?: boolean;
}

/**
 * FileUpload Component
 * 
 * Drag and drop file upload with preview, validation, and progress tracking
 */
export const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  onInsertInline,
  maxFiles = 10,
  maxFileSize = 25 * 1024 * 1024, // 25MB default
  acceptedFileTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf', 'text/plain', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip', 'application/x-zip-compressed'
  ],
  disabled = false,
  className = '',
  showPreview = true,
  allowInlineImages = true
}) => {
  const [internalFiles, setInternalFiles] = useState<UploadedFile[]>(files || []);
  const [isDragOver, setIsDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (files) {
      setInternalFiles(files);
    }
  }, [files]);

  useEffect(() => {
    attachmentService.getUploadConfig().catch(err => {
      console.warn('Failed to load upload configuration:', err);
    });
  }, []);

  const currentFiles = files && files.length > 0 ? files : internalFiles;

  // Prevent default drag behavior on the document
  useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleBodyDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'none';
    };

    const handleBodyDrop = (e: DragEvent) => {
      e.preventDefault();
    };

    // Add event listeners to prevent default browser behavior
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.addEventListener(eventName, preventDefaults, false);
    });

    document.addEventListener('dragover', handleBodyDragOver);
    document.addEventListener('drop', handleBodyDrop);

    return () => {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        document.removeEventListener(eventName, preventDefaults, false);
      });
      document.removeEventListener('dragover', handleBodyDragOver);
      document.removeEventListener('drop', handleBodyDrop);
    };
  }, []);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Validate file
  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize) {
      return `File size exceeds ${formatFileSize(maxFileSize)} limit`;
    }
    
    if (!acceptedFileTypes.includes(file.type)) {
      return `File type ${file.type} is not supported`;
    }
    
    return null;
  };

  // Process files
  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    if (disabled) return;

    const newFiles: UploadedFile[] = [];
    const fileArray = Array.from(fileList);

    // Check total file count
    if (currentFiles.length + fileArray.length > maxFiles) {
      setErrorMessage(`Maximum ${maxFiles} files exceeded`);
      return;
    }
    
    // Clear any previous errors
    setErrorMessage(null);

    for (const file of fileArray) {
      const validationError = validateFile(file);
      
      // Set global error message for validation failures and reject file
      if (validationError) {
        setErrorMessage(validationError);
        continue;
      }
      
      const uploadedFile: UploadedFile = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        isUploading: false,
        error: validationError
      };

      // Generate preview for images
      if (file.type.startsWith('image/') && showPreview && !validationError) {
        try {
          const preview = await createImagePreview(file);
          uploadedFile.preview = preview;
        } catch (error) {
          console.warn('Failed to create image preview:', error);
        }
      }

      newFiles.push(uploadedFile);
    }

    const updated = [...currentFiles, ...newFiles];
    setInternalFiles(updated);
    onFilesChange(updated);
  }, [currentFiles, onFilesChange, maxFiles, maxFileSize, acceptedFileTypes, disabled, showPreview]);

  // Create image preview
  const createImagePreview = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file input change
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      processFiles(selectedFiles);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  // Handle drag events
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    }
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  };

  // Remove file
  const removeFile = (fileId: string) => {
    if (disabled) return;
    const updated = currentFiles.filter(f => f.id !== fileId);
    setInternalFiles(updated);
    onFilesChange(updated);
  };

  // Insert image inline
  const insertInline = (file: UploadedFile) => {
    if (disabled || !file.type.startsWith('image/') || !onInsertInline) return;
    
    // Mark as inline image
    const updatedFile = { ...file, isInlineImage: true };
    onInsertInline(updatedFile);
    
    // Update the file in the list
    const updated = currentFiles.map(f => f.id === file.id ? updatedFile : f);
    setInternalFiles(updated);
    onFilesChange(updated);
  };

  // Open file picker
  const openFilePicker = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  // Get file icon
  const getFileIcon = (file: UploadedFile) => {
    if (file.type.startsWith('image/')) {
      return <PhotoIcon className="w-6 h-6 text-blue-500" />;
    }
    return <DocumentIcon className="w-6 h-6 text-gray-500" />;
  };

  const acceptString = acceptedFileTypes.join(',');

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Error Message */}
      {errorMessage && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}
      {/* Drop Zone */}
      <div
        data-testid="file-drop-zone"
        className={`
          relative border-2 border-dashed rounded-lg p-6 transition-colors duration-200
          ${isDragOver 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFilePicker}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptString}
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
          data-testid="file-input"
        />
        
        <div className="text-center">
          <PaperClipIcon className={`mx-auto h-12 w-12 ${isDragOver ? 'text-primary-400' : 'text-gray-400'}`} />
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-primary-600 hover:text-primary-500">
                Click to upload
              </span>
              {' '}or drag and drop files here
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Maximum {maxFiles} files, up to {formatFileSize(maxFileSize)} each
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Supported: Images, PDFs, Documents, Spreadsheets, Archives
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {currentFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Attached Files ({currentFiles.length}/{maxFiles})
          </h4>
          
          <div className="space-y-2">
            {currentFiles.map((file) => (
              <div
                key={file.id}
                className={`
                  flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border
                  ${file.error ? 'border-red-200 bg-red-50' : 'border-gray-200'}
                `}
              >
                {/* File Icon/Preview */}
                <div className="flex-shrink-0">
                  {file.preview ? (
                    <img
                      src={file.preview}
                      alt={`${file.name} preview`}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    getFileIcon(file)
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                  {file.error && (
                    <p className="text-xs text-red-600 mt-1">
                      {file.error}
                    </p>
                  )}
                </div>

                {/* Upload Progress */}
                {file.isUploading && (
                  <div className="flex items-center space-x-2">
                    <Loading size="sm" />
                    {file.uploadProgress !== undefined && (
                      <span className="text-xs text-gray-500">
                        {file.uploadProgress}%
                      </span>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center space-x-1">
                  {/* Insert Inline Button - Only for images */}
                  {allowInlineImages && file.type.startsWith('image/') && !file.error && !file.isUploading && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        insertInline(file);
                      }}
                      disabled={disabled}
                      className="text-blue-600 hover:text-blue-700 p-1"
                      title="Insert image inline"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </Button>
                  )}

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.id);
                    }}
                    disabled={disabled || file.isUploading}
                    className="text-gray-400 hover:text-red-500 p-1"
                    title="Remove file"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {currentFiles.length > 0 && !disabled && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Files will be uploaded when you create the ticket
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setInternalFiles([]);
              onFilesChange([]);
            }}
            className="text-red-600 hover:text-red-700"
          >
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;